/**
 * Component for Connections settings page in the Dashboard.
 *
 * @component
 */

import { Alert, Divider, Tag, message } from 'antd';
import axios from 'axios';
import React from 'react';
import { PageButtonHeader } from '../../../../components/CustomHeader';
import {
  LogoUPS,
  LogoCanadaPost,
  LogoAustraliaPost,
  LogoShippo,
  LogoShipstation,
  LogoEasyPost,
} from '../../../../components/CustomReturnsIcons';
import { IntegrationCard } from '../../../../components/IntegrationCard';
import { SubscriptionContext } from '../../../../contexts/SubscriptionContext';
import { getAppInfo } from '../../../../helpers/appInfo';
import '../../../../styles/App.css';
import CanadapostModal from './components/CanadapostModal';
import AuspostModal from './components/AuspostModal';
import ShipstationModal from './components/ShipstationModal';
import ShippoModal from './components/ShippoModal';
import EasypostModal from './components/EasypostModal';
import UPSModal from './components/UPSModal';
import { trackingVitally } from '../../../../helpers/vitally';

class ConnectCourier extends React.Component {
  static contextType = SubscriptionContext;

  constructor(props) {
    super(props);
    this.state = {
      brandSettings: {},
      connectionSettings: {},
      connectionStatus: {},
      clientId: 'efdd8904c3999b79312362f88fc31059',
      modalVisible: null,
      isProductionApp: false,
      env: null,
      developmentStore: null,
      loading: true,
    };
  }

  async fetchClientId() {
    try {
      const info = await getAppInfo();
      if (info.clientId) {
        this.setState({
          clientId: info.clientId,
        });
      }
      this.setState({
        isProductionApp: info.isProductionApp,
        env: info.env,
      });
    } catch (err) {
      message.error('error fetching clientId of application');
    }
  }

  componentDidMount = () => {
    this.initializeComponent(); // Initialize the component by calling all async functions
  };

  // Wrapper function to manage all async calls
  initializeComponent = async () => {
    try {
      // Run all async functions sequentially
      await this.fetchClientId();
      await this.loadConnectionSettings();

      // Stop loading once all functions have completed
      this.setState({ loading: false });
    } catch (err) {
      // Stop loading and handle any errors
      this.setState({ loading: false });
      message.error('Error loading connection settings. Please try again.', 4);
    }
  };

  // Function to load connection settings
  loadConnectionSettings = async () => {
    const response = await axios.get('/api/connections');
    if (response.data.status === 'error') {
      return message.error(
        'Error loading your connection settings. Try again.',
        4
      );
    }

    const connectionStatus = this.getConnectionStatus({
      connectionSettings: response.data.connectionSettings,
      carrierSettings: response.data.carrierSettings,
    });

    // Update the state with data from the API
    this.setState({
      ...response.data,
      connectionStatus,
    });
  };

  setModalVisible = (value) => {
    if (value === null) {
      this.loadConnectionSettings();
    }
    this.setState({
      modalVisible: value,
    });
  };

  getConnectionStatus = ({ connectionSettings, carrierSettings }) => {
    let connectionStatus = this.state.connectionStatus;
    connectionSettings.forEach((connection) => {
      connectionStatus = {
        ...connectionStatus,
        [connection.identifier]: connection.status,
      };
    });
    carrierSettings.forEach((carrier) => {
      connectionStatus = {
        ...connectionStatus,
        [carrier.labelService]: 'connected',
      };
    });
    return connectionStatus;
  };

  trackingEvent = (appName) => {trackingVitally('rr_appconnected');
  };

  render() {
    // console.log('connectionStatus: ', this.state.connectionStatus);
    const url = window.location.href;
    const urlObject = new URL(url);
    const urlProtocol = urlObject.protocol;
    const urlHostname = urlObject.host;
    const oAuthRedirectUrlUPS = `${urlProtocol}//${urlHostname}/dashboard/connect/ups`;

    const upsUrl =
      `${
        this.state.developmentStore ? 'https://wwwcie' : 'https://onlinetools'
      }.ups.com/security/v1/oauth/authorize?` +
      'client_id=0blYFDlAbrXE2itGOCfbJfRfBWgwDq4llaCxkohBrlZcFHHz' +
      `&redirect_uri=${oAuthRedirectUrlUPS}` +
      '&response_type=code';
    let data = [
      {
        title: (
          <>
            UPS <Tag color="purple">New</Tag>
          </>
        ),
        identifier: 'ups',
        oAuthUrl: upsUrl,
        logo: <LogoUPS></LogoUPS>,
        description:
          'Connect to UPS to streamline your return process with seamless shipping and tracking.',
      },
      {
        title: (
          <>
            Canada Post <Tag color="purple">New</Tag>
          </>
        ),
        identifier: 'canadapost',
        logo: <LogoCanadaPost></LogoCanadaPost>,
        description:
          'Connect to Canada Post to streamline your return process with seamless shipping and tracking.',
      },
      {
        title: (
          <>
            Australia Post <Tag color="purple">New</Tag>
          </>
        ),
        identifier: 'auspost',
        // oAuthUrl: upsUrl,
        logo: <LogoAustraliaPost></LogoAustraliaPost>,
        description:
          'Connect to Auspost to streamline your return process with seamless shipping and tracking.',
      },
      {
        title: (
          <>
            Shippo <Tag color="purple">New</Tag>
          </>
        ),
        identifier: 'shippo',
        // oAuthUrl: upsUrl,
        logo: <LogoShippo></LogoShippo>,
        description:
          'Connect to Shippo to streamline your return process with seamless shipping and tracking.',
      },
      {
        title: (
          <>
            ShipStation <Tag color="purple">New</Tag>
          </>
        ),
        identifier: 'shipstation',
        logo: <LogoShipstation></LogoShipstation>,
        description:
          'Connect to ShipStation to streamline your return process with seamless shipping and tracking.',
      },
      {
        title: (
          <>
            EasyPost <Tag color="purple">New</Tag>
          </>
        ),
        identifier: 'easypost',
        // oAuthUrl: upsUrl,
        logo: <LogoEasyPost></LogoEasyPost>,
        description:
          'Connect to EasyPost to streamline your return process with seamless shipping and tracking.',
      },
    ];

    if (this.state.loading) {
      return (
        <div style={{ position: 'absolute', top: '50%', left: '50%' }}>
          <div className="GlobalLoader GlobalLoader--md"></div>
        </div>
      );
    }

    return (
      <React.Fragment>
        <PageButtonHeader headingTitle={'Connect carrier'}></PageButtonHeader>
        <Alert
          message="Looking for Return Labels?"
          description={
            <span>
              Head over to <strong>Configuration / Prepaid Labels</strong> to
              set up the integration with one of our supported shipping
              carriers.
            </span>
          }
          type="info"
          showIcon
        />
        <div style={{ marginTop: 30 }}>
          <div className="flex-row">
            {data.map((item) => {
              return (
                <div
                  className="flex-col-xxs-12 flex-col-xs-12 flex-col-sm-6 flex-col-md-4 flex-col-lg-3 u-displayFlex u-flexAlignItems--stretch"
                  key={item.identifier}
                >
                  <IntegrationCard
                    item={item}
                    connectionStatus={this.state.connectionStatus}
                    modalVisible={this.setModalVisible}
                    trackingEvent={this.trackingEvent}
                  ></IntegrationCard>
                </div>
              );
            })}
          </div>
        </div>
        <Divider />
        <div className="TextBody TextBody--xxs u-textCenter--all">
          Missing a service?
          <a href="mailto:hello@richcommerce.co"> Let us know here.</a>
        </div>
        {this.state.modalVisible === 'canadapost' ? (
          <CanadapostModal
            modalForKlaviyoVisible={this.state.modalVisible === 'klaviyo'}
            setModalVisible={this.setModalVisible}
          />
        ) : null}
        {this.state.modalVisible === 'auspost' ? (
          <AuspostModal
            modalVisible={this.state.modalVisible === 'auspost'}
            setModalVisible={this.setModalVisible}
          />
        ) : null}
        {this.state.modalVisible === 'shipstation' ? (
          <ShipstationModal
            modalVisible={this.state.modalVisible === 'shipstation'}
            setModalVisible={this.setModalVisible}
          />
        ) : null}
        {this.state.modalVisible === 'shippo' ? (
          <ShippoModal
            modalVisible={this.state.modalVisible === 'shippo'}
            setModalVisible={this.setModalVisible}
          />
        ) : null}
        {this.state.modalVisible === 'easypost' ? (
          <EasypostModal
            modalVisible={this.state.modalVisible === 'easypost'}
            setModalVisible={this.setModalVisible}
          />
        ) : null}
        {this.state.modalVisible === 'ups' ? (
          <UPSModal
            modalVisible={this.state.modalVisible === 'ups'}
            setModalVisible={this.setModalVisible}
            upsUrl={upsUrl}
          />
        ) : null}
      </React.Fragment>
    );
  }
}

export default ConnectCourier;
